// g2-dispatch-gate-enforcement.ts
//
// G2 (P16): turns `decideG2Requirement`'s verdict into persisted gate state.
// Called once per tick from supervisor-run-until-idle.ts, BEFORE dispatch, so
// the guard (feature-dispatch-gate-guard.ts) reads a settled g2 record.
//
// H4 A1 (design bản 2, 2026-09-29) — the approval is bound to the plan:
//   1. Nothing is requested or waived until the plan is READY (every
//      bootstrap ticket done — g2-plan-readiness.ts). The readiness is
//      returned so the guard can hold feature tickets meanwhile.
//   2. The request id is `deriveG2RequestId(missionId, H)`, H = hash of the
//      plan content snapshot (g2-plan-content-snapshot.ts), written durably
//      first; the mission id is in the seed since close-out 8b.
//   3. pending/approved for another H (or for no H — a request/approval made
//      before this change, D2) ⇒ a fresh request for the current H, with the
//      reason and the ticket-level diff. An approval is NEVER carried over to
//      new content, and never downgraded to a waive.
//   4. `invalidated` (a redirect changed the plan) is no longer a dead end:
//      it is re-requested for the current H.
// B2-5 (see g2-plan-gate-decision.ts): destructive/migration work is unknown
// in v1, so G2 is never waived here; a waive path comes back only with a
// trustworthy declaration (backlog D-G1 b) — and then as delegated authority
// (`authority_ref`, no `actor_id`, spec §8).
import { randomUUID } from "node:crypto";
import { g2DagRuleNotes } from "./g2-dag-rule-notes.js";
import { measureG2Decision } from "./g2-decision-with-high-water-marks.js";
import {
  buildG2PlanSnapshot,
  controllerAuthoredDecisionTicketIds,
  hashG2PlanSnapshot,
  writeG2PlanSnapshot,
} from "./g2-plan-content-snapshot.js";
import { assessG2PlanReadiness, type G2PlanReadiness } from "./g2-plan-readiness.js";
import { g2RequestReason } from "./g2-request-reason-text.js";
import { journalUnenforcedG2Conditions } from "./g2-unenforced-conditions-observability.js";
import { deriveGateRequestId, type GateFile, readGateFile, requestGate } from "./gate-registry.js";
import type { MissionState } from "./mission-state-reducer.js";
import type { MissionStore } from "./mission-store.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export interface EnsureG2DecisionRecordedInput {
  controlRoot: string;
  /** Execution repo worktree — source of `.avatar/verification-profile.json` (§8 condition 3, and the recipes G2 approves). */
  repoDir: string;
  missionId: string;
  tickets: Ticket[];
  ticketStatus: Record<string, string>;
  g2TicketThreshold: number;
  g0Delegated: boolean;
  /** P25 journal sink (unmeasured §8 conditions) and H4 `g2_approval_superseded` receipts. */
  store?: Pick<MissionStore, "commit">;
  /**
   * D5: the journal ledger that marks controller-made redirect decision
   * tickets (absent ⇒ none excluded); `results` lets a replay see a
   * `g2_approval_superseded` receipt it already journaled (H4 T11).
   */
  missionState?: Pick<MissionState, "intents" | "results">;
  now?: () => Date;
}

export interface EnsureG2DecisionRecordedResult {
  /** What the gate record says after this call. */
  status: GateFile["g2"]["status"];
  /** false when an existing record was left untouched (idempotent no-op). */
  changed: boolean;
  reasons: string[];
  /** P25: §8 conditions this controller cannot measure (see g2-unenforced-conditions-observability.ts). */
  unenforcedConditions: readonly string[];
  /** H4: whether the plan exists yet — the guard holds feature tickets until it does. */
  planReadiness: G2PlanReadiness;
}

export async function ensureG2DecisionRecorded(
  input: EnsureG2DecisionRecordedInput,
): Promise<EnsureG2DecisionRecordedResult> {
  const gates = await readGateFile(input.controlRoot);
  // Bug L/P17/P25 high-water marks + decideG2Requirement (ratchet up every
  // tick, so they are measured even before the plan is ready).
  const decision = await measureG2Decision({
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    tickets: input.tickets,
    ticketStatus: input.ticketStatus,
    g2TicketThreshold: input.g2TicketThreshold,
    g0Delegated: input.g0Delegated,
  });
  if (input.store) await journalUnenforcedG2Conditions(input.store, decision.unenforcedConditions);

  const planReadiness = assessG2PlanReadiness(
    input.tickets,
    input.ticketStatus,
    controllerAuthoredDecisionTicketIds(
      input.tickets,
      input.missionState ?? { intents: {}, results: {} },
    ),
  );
  const g2 = gates.g2;
  const result = (status: GateFile["g2"]["status"], changed: boolean) => ({
    status,
    changed,
    reasons: decision.reasons,
    unenforcedConditions: decision.unenforcedConditions,
    planReadiness,
  });
  if (!planReadiness.ready) return result(g2.status, false);

  const snapshot = await buildG2PlanSnapshot({
    repoDir: input.repoDir,
    tickets: input.tickets,
    ticketStatus: input.ticketStatus,
    state: input.missionState ?? { intents: {}, results: {} },
  });
  const hash = hashG2PlanSnapshot(snapshot);

  const boundToThisPlan =
    (g2.status === "approved" || g2.status === "pending") && g2.artifact_hash === hash;
  if (boundToThisPlan) return result(g2.status, false);
  // B2-5: `decision.waivable` is always false in v1, so there is no waive
  // branch here any more (review L9 — it was unreachable and untested). A
  // `waived` record from an older CLI falls through and is re-requested.
  const requestId = deriveG2RequestId(input.missionId, hash);
  // V2 §4.3 (E5) + 06j R3: DAG rules 5–6 are read-only here — appended to the
  // reason AFTER the hash above, so they never move the request id or snapshot.
  const reason = await g2RequestReason(input.controlRoot, g2, snapshot, [
    ...decision.reasons,
    ...g2DagRuleNotes(input.tickets, input.ticketStatus, input.missionId),
  ]);
  await writeG2PlanSnapshot(input.controlRoot, hash, snapshot);
  // One receipt per superseded APPROVAL (its id + time — review L1: keyed on
  // the new id alone, A→B→A→B lost the second one). H4 T11: a run killed
  // after this receipt but before requestGate replays here with the same g2
  // record, so the receipt is already in the journal — write it once.
  const supersededId = `g2-approval-superseded-${requestId}-from-${g2.request_id}@${g2.at ?? "?"}`;
  const alreadyJournaled = input.missionState?.results[supersededId] !== undefined;
  if (g2.status === "approved" && input.store && !alreadyJournaled) {
    await input.store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "operation_result",
          operation_id: supersededId,
          operation_kind: "g2_approval_superseded",
          outcome: "superseded",
          previous_request_id: g2.request_id,
          previous_artifact_hash: g2.artifact_hash ?? null,
          request_id: requestId,
          artifact_hash: hash,
          reason,
        },
      },
    ]);
  }
  // 8c: the request itself reaches the journal (timeline/telemetry), BEFORE
  // the gate write so a kill in between replays here — once per request. The
  // key carries the record this request REPLACES (id + time, like the
  // superseded receipt): a replay sees the same record ⇒ the same key, while
  // the plan coming back later (A → B → A, same id for A) replaces a different
  // record ⇒ a new key, so the second request is journaled too (review P4 L3).
  const requestedId = `g2-requested-${requestId}-from-${g2.request_id ?? "none"}@${g2.at ?? "?"}`;
  if (input.store && input.missionState?.results[requestedId] === undefined) {
    await input.store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "operation_result",
          operation_id: requestedId,
          operation_kind: "g2_requested",
          outcome: "pending",
          request_id: requestId,
          artifact_hash: hash,
          reason,
        },
      },
    ]);
  }
  await requestGate(input.controlRoot, "g2", requestId, hash, input.now, reason);
  return result("pending", true);
}

/**
 * 8b (close-out 2026-09-30): the G2 request id is seeded with the mission id
 * too — the plan hash alone gave two missions with the same plan the same id.
 * A record already bound to the plan hash is kept as-is (`boundToThisPlan`
 * compares hashes, not ids), so an older hash-only id stays approvable.
 */
export function deriveG2RequestId(missionId: string, planHash: string): string {
  return deriveGateRequestId("g2", `${missionId}:${planHash}`);
}
